import type { LocalOrSavedFile } from '@frontend/api-types';
import { match } from '@frontend/duck-tape';
import type { GeneralFileType } from '@frontend/forms';
import { getGeneralFileType } from '@frontend/forms';
import { useState } from '@frontend/react';
import { Header, Image, LoadingSpinnerPanel, Modal, SkeletonList, Text, YStack } from '@frontend/web-react';
import React from 'react';
import { Page, Document as Pdf, pdfjs } from 'react-pdf';
import type { CommonModalProps } from './types';

// eslint-disable-next-line fp/no-mutation
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export type FilePreviewModalProps = CommonModalProps & {
  file: MaybeUndefined<LocalOrSavedFile>;
  title: MaybeUndefined<string>;
};

const options = {
  cMapUrl: '/cmaps/',
};

export const FilePreviewModal = ({ file, isOpen, onClose, title = 'Uploaded File' }: FilePreviewModalProps) => {
  const [numPages, setNumPages] = useState<number>();
  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  const PdfPreview = () =>
    file ? (
      <Pdf
        error={() => <Text>The pdf could not be loaded.</Text>}
        file={file.url}
        loading={<LoadingSpinnerPanel />}
        onLoadSuccess={onDocumentLoadSuccess}
        options={options}
      >
        <YStack gapY="sm">
          {Array.from({ length: numPages || 0 }).map((_, index, array) => (
            <YStack className="p-md border-1 border-borderPrimary" key={index}>
              <Text color="tertiary" textAlign="end" type="captionBold">
                {`${index + 1} of ${array.length}`}
              </Text>
              <Page loading={<SkeletonList countSkeletons={10} />} pageNumber={index + 1} width={480} />
            </YStack>
          ))}
        </YStack>
      </Pdf>
    ) : null;

  const ImagePreview = () =>
    // eslint-disable-next-line tailwindcss/no-arbitrary-value
    file ? <Image className="max-h-[600px] min-h-[250px] object-contain" source={file.url} /> : null;

  const AudioPreview = () =>
    file ? (
      <YStack className="flex-row justify-center items-center pt-md pb-xxl">
        <audio controls src={file.url} />
      </YStack>
    ) : null;

  const VideoPreview = () =>
    file ? (
      <YStack className="flex-row justify-center items-center pt-md pb-xxl">
        <video controls src={file.url} />
      </YStack>
    ) : null;

  const generalFileType = file ? getGeneralFileType(file) : undefined;

  return (
    <Modal
      Header={
        <Header
          onClickBack={onClose}
          // eslint-disable-next-line no-restricted-globals
          onClickRightIcon={() => file && open(file.url, '_blank')}
          rightIconName="IconExternalLink"
          title={title}
        />
      }
      isOpen={isOpen}
      onClose={onClose}
      scrollViewContainerClassName="justify-center"
      size={'600px'}
      title={title}
      trapFocus
    >
      {
        <YStack>
          {generalFileType
            ? match<GeneralFileType, React.ReactNode>(generalFileType)
                .with('image', () => <ImagePreview />)
                .with('pdf', () => <PdfPreview />)
                .with('audio', () => <AudioPreview />)
                .with('video', () => <VideoPreview />)
                .otherwise(() => (
                  <YStack className="flex-row justify-center items-center pt-md pb-xxl">
                    <Text>No preview available</Text>
                  </YStack>
                ))
            : null}
        </YStack>
      }
    </Modal>
  );
};
